/**
 * TileSkeleton — type-aware loading placeholder so each tile's skeleton
 * pre-figures what the rendered viz will look like (not a generic gray block).
 * Uses Chakra's Skeleton + SkeletonText which already shimmer.
 */
import React from 'react';
import { Box, Skeleton, HStack, VStack, Flex } from '@chakra-ui/react';

function BigNumberSkel() {
  return (
    <VStack align="stretch" spacing={2}>
      <Skeleton height="34px" w="60%" borderRadius="md" />
      <Skeleton height="12px" w="40%" />
      <Skeleton height="10px" w="30%" />
    </VStack>
  );
}

function LineChartSkel() {
  // Mimic a line chart: rising bars of variable height with shimmer + an axis line.
  const heights = [40, 55, 48, 62, 50, 68, 72, 58, 80, 70, 85, 78];
  return (
    <VStack align="stretch" spacing={2} h="220px">
      <Box flex={1} position="relative">
        <HStack spacing={1} align="flex-end" h="100%">
          {heights.map((h, i) => (
            <Skeleton key={i} h={`${h}%`} flex={1} borderTopRadius="sm" />
          ))}
        </HStack>
      </Box>
      <Skeleton h="1px" />
      <HStack spacing={4}>
        <Skeleton h="8px" w="30px" /><Skeleton h="8px" w="30px" /><Skeleton h="8px" w="30px" /><Skeleton h="8px" w="30px" />
      </HStack>
    </VStack>
  );
}

function BreakdownBarSkel() {
  // 6 horizontal bars of decreasing width — exactly the shape of a Top-N bar chart.
  const widths = ['95%', '78%', '62%', '50%', '40%', '32%'];
  return (
    <VStack align="stretch" spacing={3}>
      {widths.map((w, i) => (
        <HStack key={i} spacing={2}>
          <Skeleton h="10px" w="80px" />
          <Skeleton h="14px" w={w} borderRadius="sm" />
        </HStack>
      ))}
    </VStack>
  );
}

function TableSkel({ rows = 6, cols = 4 }) {
  return (
    <VStack align="stretch" spacing={2}>
      <HStack spacing={3}>
        {Array.from({ length: cols }).map((_, i) => <Skeleton key={i} h="10px" flex={1} />)}
      </HStack>
      <Skeleton h="1px" />
      {Array.from({ length: rows }).map((_, r) => (
        <HStack key={r} spacing={3}>
          {Array.from({ length: cols }).map((_, c) => (
            <Skeleton key={c} h="14px" flex={c === 0 ? 1.5 : 1} startColor={c === 0 ? 'gray.100' : 'gray.50'} />
          ))}
        </HStack>
      ))}
    </VStack>
  );
}

function FunnelSkel() {
  // Stacked rectangles narrowing — looks like a funnel.
  const widths = ['100%', '78%', '58%', '40%'];
  return (
    <VStack align="center" spacing={2}>
      {widths.map((w, i) => (
        <Skeleton key={i} h="40px" w={w} borderRadius="sm" />
      ))}
    </VStack>
  );
}

function ChannelReachSkel() {
  return (
    <VStack align="stretch" spacing={3}>
      <Skeleton h="28px" w="40%" />
      {[1, 2, 3, 4].map((i) => (
        <Box key={i}>
          <Flex justify="space-between" mb={1}><Skeleton h="10px" w="60px" /><Skeleton h="10px" w="50px" /></Flex>
          <Skeleton h="8px" borderRadius="md" />
        </Box>
      ))}
    </VStack>
  );
}

const SKELETONS = {
  big_number:           BigNumberSkel,
  time_series:          LineChartSkel,
  retention:            LineChartSkel,
  breakdown:            BreakdownBarSkel,      // single-metric form
  funnel:               FunnelSkel,
  kpi_table:            (p) => <TableSkel rows={6} cols={5} {...p} />,
  segment_leaderboard:  (p) => <TableSkel rows={8} cols={4} {...p} />,
  channel_reachability: ChannelReachSkel,
  profile_breakdown:    BreakdownBarSkel,
  distribution:         BreakdownBarSkel,
};

export default function TileSkeleton({ tile }) {
  const C = SKELETONS[tile?.type] || BreakdownBarSkel;
  // breakdown tiles with metrics[] render as a table — show TableSkel instead of bars.
  if (tile?.type === 'breakdown' && Array.isArray(tile?.viz_config?.metrics) && tile.viz_config.metrics.length > 1) {
    return <TableSkel rows={6} cols={1 + tile.viz_config.metrics.length} />;
  }
  return <C />;
}
